/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  ActionsService,
  BookmarksService,
  type ContentScreenDetails,
  type TActionsContentScreenDetailsContentIdParams,
  type TActionsContentScreenDetailsOfferIdParams,
  type TActionsContentScreenDetailsRecordingIdParams,
  type TActionsContentScreenDetailsVodOfferParams,
  type TActionsParamsUCScreenDetails,
  type TUnifiedCollectionScreenDetails
} from "@tivocorp/data-domain-lib";
import type { Action } from "@tivocorp/data-domain-lib/client-code/actions";

import type {
  IContentDetailInteractor,
  TContentDetailFavoriteTarget
} from "../../interfaces/IContentDetailInteractor";
import { CONTENT_DETAIL_FAVORITE_MODE } from "../../interfaces/IContentDetailInteractor";
import type { TNetworkOptions } from "../../types/TNetworkOptions";
import { CONTENT_DETAIL_SEED_TYPE } from "../../types";
import { Log } from "../../logging/Log";
import { ApplicationFeatureArea } from "../../enums";
import { TIVO_SERVICES } from "../../../config";
import { BaseInteractor } from "../base";
import { ServiceLocator } from "../base/ServiceLocator";

/**
 * Shared, atomic interactor wrapping {@link ActionsService} for the
 * Content Detail screen.
 *
 * Modeled on the shared PreviewInteractor pattern: stateless, a fresh
 * `ActionsService` is built per call via {@link ServiceLocator}, and the
 * {@link ApplicationFeatureArea} is fixed at construction time so the
 * `applicationFeatureArea` request header matches the calling feature.
 *
 * Only `contentId` and `unifiedCollectionId` seeds are supported — see
 * {@link IContentDetailInteractor} for the rationale. offerId / recordingId
 * / vodOfferId are rejected earlier, by the contentDetailSlice thunk, and
 * intentionally have no fetch method on this interactor.
 */
export class ContentDetailInteractor
  extends BaseInteractor
  implements IContentDetailInteractor
{
  private static readonly TAG: string = "ContentDetailInteractor";

  /**
   * @param mFeatureArea - ApplicationFeatureArea reported in the
   *                       request header for every call made by this
   *                       instance. Defaults to {@link ApplicationFeatureArea.ALL}.
   */
  public constructor(
    private readonly mFeatureArea: ApplicationFeatureArea = ApplicationFeatureArea.ALL
  ) {
    super();
  }

  /** Builds a per-call `ActionsService` bound to this interactor's feature area. */
  private async buildActionsService(): Promise<ActionsService> {
    return ServiceLocator.getInstance(
      ActionsService,
      await this.buildServicesParams(
        TIVO_SERVICES.ActionService,
        this.mFeatureArea
      )
    );
  }

  public async updateFavorite(
    target: TContentDetailFavoriteTarget,
    options?: TNetworkOptions
  ): Promise<void> {
    return this.executeGuarded(
      "updateFavorite",
      async (signal: AbortSignal): Promise<void> => {
        const service = ServiceLocator.getInstance(
          BookmarksService,
          await this.buildServicesParams(
            TIVO_SERVICES.BookmarksService,
            this.mFeatureArea
          )
        );
        switch (target.mode) {
          case CONTENT_DETAIL_FAVORITE_MODE.ADD:
            await service.createBookmarkSchedule(
              {
                contentId: target.contentId,
                collectionId: target.collectionId
              },
              signal
            );
            break;
          case CONTENT_DETAIL_FAVORITE_MODE.REMOVE:
            await service.deleteBookmarkSchedule(
              { contentId: target.contentId },
              signal
            );
            break;
          case CONTENT_DETAIL_FAVORITE_MODE.SEASON:
            await service.createUnifiedCollectionBookmarkSchedule(
              {
                unifiedCollectionId: target.unifiedCollectionId,
                seasonOrYear: { season: target.season }
              },
              signal
            );
            break;
          case CONTENT_DETAIL_FAVORITE_MODE.YEAR:
            await service.createUnifiedCollectionBookmarkSchedule(
              {
                unifiedCollectionId: target.unifiedCollectionId,
                seasonOrYear: { year: target.year }
              },
              signal
            );
            break;
        }
        ActionsService.clearCache();
      },
      options
    );
  }

  /**
   * @inheritdoc
   */
  public async fetchContentScreenDetails(
    contentId: string,
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<ContentScreenDetails> {
    return this.executeGuarded(
      "fetchContentScreenDetails",
      async (guardedSignal: AbortSignal): Promise<ContentScreenDetails> => {
        Log.d(
          ContentDetailInteractor.TAG,
          "fetchContentScreenDetails() called"
        );
        const service: ActionsService = await this.buildActionsService();
        const params: TActionsContentScreenDetailsContentIdParams = {
          contentId
        };
        return service.fetchContentScreenDetailsContentActionsV3(
          params,
          isForced,
          guardedSignal
        );
      },
      options
    );
  }

  /**
   * @inheritdoc
   */
  public async fetchOfferScreenDetails(
    offerId: string,
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<ContentScreenDetails> {
    return this.executeGuarded(
      "fetchOfferScreenDetails",
      async (guardedSignal: AbortSignal): Promise<ContentScreenDetails> => {
        Log.d(ContentDetailInteractor.TAG, "fetchOfferScreenDetails() called");
        const service: ActionsService = await this.buildActionsService();
        const params: TActionsContentScreenDetailsOfferIdParams = { offerId };
        return service.fetchContentScreenDetailsOfferActionsV3(
          params,
          isForced,
          guardedSignal
        );
      },
      options
    );
  }

  /**
   * @inheritdoc
   */
  public async fetchRecordingScreenDetails(
    recordingId: string,
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<ContentScreenDetails> {
    return this.executeGuarded(
      "fetchRecordingScreenDetails",
      async (guardedSignal: AbortSignal): Promise<ContentScreenDetails> => {
        Log.d(
          ContentDetailInteractor.TAG,
          "fetchRecordingScreenDetails() called"
        );
        const service: ActionsService = await this.buildActionsService();
        const params: TActionsContentScreenDetailsRecordingIdParams = {
          recordingId
        };
        return service.fetchContentScreenDetailsRecordingActionsV3(
          params,
          isForced,
          guardedSignal
        );
      },
      options
    );
  }

  /**
   * @inheritdoc
   */
  public async fetchVodOfferScreenDetails(
    vodOfferId: string,
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<ContentScreenDetails> {
    return this.executeGuarded(
      "fetchVodOfferScreenDetails",
      async (guardedSignal: AbortSignal): Promise<ContentScreenDetails> => {
        Log.d(
          ContentDetailInteractor.TAG,
          "fetchVodOfferScreenDetails() called"
        );
        const service: ActionsService = await this.buildActionsService();
        const params: TActionsContentScreenDetailsVodOfferParams = {
          vodOfferId
        };
        return service.fetchContentScreenDetailsVodOfferActions(
          params,
          isForced,
          guardedSignal
        );
      },
      options
    );
  }

  /**
   * @inheritdoc
   */
  public async fetchScreenDetailsForSeed(
    seed: { seedType: string; seedId: string },
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<ContentScreenDetails> {
    switch (seed.seedType) {
      case CONTENT_DETAIL_SEED_TYPE.OFFER_ID:
        return this.fetchOfferScreenDetails(seed.seedId, isForced, options);
      case CONTENT_DETAIL_SEED_TYPE.RECORDING_ID:
        return this.fetchRecordingScreenDetails(seed.seedId, isForced, options);
      case CONTENT_DETAIL_SEED_TYPE.VOD_OFFER_ID:
        return this.fetchVodOfferScreenDetails(seed.seedId, isForced, options);
      default:
        return this.fetchContentScreenDetails(seed.seedId, isForced, options);
    }
  }

  /**
   * @inheritdoc
   */
  public async fetchUnifiedCollectionScreenDetails(
    unifiedCollectionId: string,
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<TUnifiedCollectionScreenDetails> {
    return this.executeGuarded(
      "fetchUnifiedCollectionScreenDetails",
      async (
        guardedSignal: AbortSignal
      ): Promise<TUnifiedCollectionScreenDetails> => {
        Log.d(
          ContentDetailInteractor.TAG,
          "fetchUnifiedCollectionScreenDetails() called"
        );
        const service: ActionsService = await this.buildActionsService();
        const params: TActionsParamsUCScreenDetails = {
          unifiedCollectionId
        };
        return service.fetchUnifiedCollectionScreenDetailsActionsV4(
          params,
          isForced,
          guardedSignal
        );
      },
      options
    );
  }

  /**
   * @inheritdoc
   */
  public async resolvePlayableEpisodeActions(
    unifiedCollectionId: string,
    _options?: TNetworkOptions
  ): Promise<Action[] | null> {
    // TODO: not yet available in @tivocorp/data-domain-lib — replace with
    // the dedicated playable-episode resolver once it exists; the mapper
    // falls back to actionUnifiedCollectionDetail.actions from the
    // screen-details response.
    Log.d(
      ContentDetailInteractor.TAG,
      "resolvePlayableEpisodeActions() not implemented for unifiedCollectionId=" +
        unifiedCollectionId +
        " — see TODO comment; returning null"
    );
    return null;
  }
}
